import {
  IconWrapper,
  formatHumanDate,
  formatToMaxFourDigits,
} from '@flexo/general';
import { TableFooter, TableSortLabel } from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import parse from 'html-react-parser';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './table.module.scss';

import { RIGHT_DRAWER, store } from '@flexo/reducers';
import { useSelector } from 'react-redux';
import { TableSkeleton } from '../skeletons/table-skeleton';
import { useDrawerTableSpacer } from './use-drawer-table-spacer';

function isValidInvoice(site: any) {
  const value = `${site?.is_valid}`.toLowerCase();
  return (
    site?.is_valid !== false &&
    value !== 'f' &&
    value !== 'false' &&
    value !== '0'
  );
}

function getDateTime(value: any) {
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
}

function sortInvoicePeriodRows(
  rowsToSort: Array<any>,
  columnId: string,
  direction: string
) {
  const orderFactor = direction === 'asc' ? 1 : -1;

  return [...rowsToSort].sort((a, b) => {
    let result = 0;

    switch (columnId) {
      case 'amount':
        result = (a.amount || 0) - (b.amount || 0);
        break;
      case 'received':
        result = (a.received || 0) - (b.received || 0);
        break;
      case 'paid':
        result =
          (a.paidRatio || 0) - (b.paidRatio || 0) ||
          (a.paidCount || 0) - (b.paidCount || 0) ||
          (a.invoiceCount || 0) - (b.invoiceCount || 0);
        break;
      case 'period':
        result =
          getDateTime(a.start_period) - getDateTime(b.start_period) ||
          getDateTime(a.end_period) - getDateTime(b.end_period);
        break;
    }

    if (result === 0) {
      return (a.index || 0) - (b.index || 0);
    }

    return result * orderFactor;
  });
}

export function InvoicesPeriodTable(props: any) {
  const { t } = useTranslation();
  const dispatch = store.dispatch;
  const metadata = useSelector((state: any) => state.metadata);
  const rightDrawer = useSelector((state: any) => state.rightDrawer);

  const { subtitle, no_data_text, rows } = t(
    `widgets.tables.InvoicesPeriodTable`,
    {
      returnObjects: true,
    }
  ) as any;

  const title = t('widgets.tables.InvoicesPeriodTable.title', {
    communityName: metadata.selectedEntity?.communityName,
  });

  const columns = rows;

  const [storedRows, setStoredRows] = useState<Array<any>>([]);
  const [rowsOrder, setRowsOrder] = useState(['period', 'desc']);
  const { bodyRef, containerRef, footerRef, headRef, spacerHeight } =
    useDrawerTableSpacer(storedRows.length > 0);

  useEffect(() => {
    const tableData = props.data.map((period: any, periodIndex: number) => {
      const sites = Array.isArray(period.sites) ? period.sites : [];
      const validSites = sites.filter(isValidInvoice);
      const paidCount = validSites.filter((site: any) => site?.paid).length;
      const invoiceCount = validSites.length;

      return {
        ...period,
        index: periodIndex,
        period: period.period,
        amount: validSites.reduce(
          (acc: number, site: any) => acc + (site.amount || 0),
          0
        ),
        received: validSites.reduce(
          (acc: number, site: any) => acc + (site?.paid ? site.amount || 0 : 0),
          0
        ),
        paid: `${paidCount}/${invoiceCount}`,
        paidCount,
        invoiceCount,
        paidRatio: invoiceCount > 0 ? paidCount / invoiceCount : 0,
        start_period:
          period.period_start_date ||
          validSites[0]?.period_start_date ||
          sites[0]?.period_start_date,
        end_period:
          period.period_end_date ||
          validSites[0]?.period_end_date ||
          sites[0]?.period_end_date,
      };
    });

    setRowsOrder(['period', 'desc']);
    setStoredRows(sortInvoicePeriodRows(tableData, 'period', 'desc'));
  }, [props.data]);

  function sortRows(columnId: string) {
    try {
      let direction = 'asc';

      if (rowsOrder[0] === columnId && rowsOrder[1] === 'asc') {
        direction = 'desc';
      }

      setRowsOrder([columnId, direction]);
      setStoredRows(sortInvoicePeriodRows(storedRows, columnId, direction));
    } catch (err: any) {
      // console.warn(err)
    }
  }

  return (
    <div className={styles.Table__DrawerViewport}>
      {/* {LOADING_STATE === 'PENDING' && <Shimmer />} */}
      {storedRows?.length > 0 ? (
        <div className={styles.Table__InvoicesPeriods__OverflowContainer}>
          <div
            className={`${styles.Table} ${styles.Table__Flex} ${styles.Table__InvoicesPeriods}`}
          >
            <div className={`${styles.Table__InvoicesPeriods__TitleContainer}`}>
              <div className={`${styles.Table__InvoicesPeriods__Title}`}>
                {parse(title)}
              </div>
              <div className={`${styles.Table__InvoicesPeriods__Subtitle}`}>
                {subtitle}
              </div>
              <div
                className={`${styles.Table__InvoicesPeriods__Close}`}
                onClick={() => {
                  dispatch(RIGHT_DRAWER.ResetDrawer());
                }}
              >
                <IconWrapper iconName="cross" />
              </div>
            </div>

            <TableContainer
              component={Paper}
              className={styles.Table__InvoicesPeriods__TableContainer}
              ref={containerRef}
            >
              <Table aria-label="simple table">
                <TableHead
                  className={styles.Table__InvoicesPeriods__Head}
                  ref={headRef}
                >
                  <TableRow>
                    {columns &&
                      columns.map((column: any, column_i: number) => (
                        <TableCell
                          className={`
                ${
                  column.sortable
                    ? styles.Table__Header__Cell__Sortable
                    : styles.Table__Header__Cell__Static
                }
              ${
                column.sortable && rowsOrder[0] === column.field
                  ? styles.Table__Header__Cell__Active
                  : styles.Table__Header__Cell
              }`}
                          key={`invoices_periods_table_${column_i}`}
                        >
                          <TableSortLabel
                            active={
                              column.sortable && rowsOrder[0] === column.field
                            }
                            direction={
                              rowsOrder[0] === column.field &&
                              rowsOrder[1] === 'asc'
                                ? 'asc'
                                : 'desc'
                            }
                            onClick={() =>
                              column.sortable ? sortRows(column.field) : null
                            }
                            className={`${styles.Table__Header__Cell}`}
                            IconComponent={() =>
                              rowsOrder[0] === column.field ? (
                                rowsOrder[1] === 'asc' ? (
                                  <IconWrapper
                                    iconName="chevron-up"
                                    className={`${styles.Table__TH__Icon}`}
                                  />
                                ) : (
                                  <IconWrapper
                                    iconName="chevron-down"
                                    className={`${styles.Table__TH__Icon}`}
                                  />
                                )
                              ) : (
                                <div style={{ height: 32, width: 19 }}></div>
                              )
                            }
                          >
                            <div> {column.title}</div>
                          </TableSortLabel>
                        </TableCell>
                      ))}
                  </TableRow>
                </TableHead>
                <TableBody ref={bodyRef}>
                  {(storedRows || []).map((row) => (
                    <TableRow
                      key={`invoices_periods_table_row_${row.period}_${row.index}`}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      style={{ height: 100 }}
                      data-table-body-row="true"
                      onClick={() =>
                        dispatch(
                          RIGHT_DRAWER.SetDrawer({
                            type: 'INVOICES_PERIOD_DETAILS',
                            drawerConfig: {
                              data: row,
                              oldData: rightDrawer.drawerConfig,
                            },
                          } as any) as any
                        )
                      }
                      className={`${styles.Table__Body__Row__Clickable}`}
                    >
                      <TableCell
                        className={`${
                          !columns?.[0]?.sortable === true &&
                          styles.Table__Cell__Static
                        }`}
                        key={`invoices_periods_table_row_${row.period}_cell_0_`}
                        component="th"
                        scope="row"
                      >
                        <div
                          className={`${styles.Table__Cell__InvoicesPeriod__Period}`}
                        >
                          <span>{row.period}</span>
                          <span
                            className={`${styles.Table__Cell__InvoicesPeriod__Period__Due}`}
                          >
                            {t('widgets.tables.utils.due_date')}:{' '}
                            {formatHumanDate(row.sites?.[0]?.due_date)}
                          </span>
                        </div>
                      </TableCell>

                      <TableCell
                        key={`invoices_periods_table_row_${row.period}_cell_1`}
                        component="th"
                        scope="row"
                      >
                        <div
                          className={`${
                            !columns?.[0]?.sortable === true &&
                            styles.Table__Cell__Static
                          }`}
                        >
                          <p
                            className={`${styles.Table__Cell__InvoicesPeriod__Amount}`}
                          >
                            {row?.sites?.[0]?.unit
                              ? ` ${row.sites[0].unit}`
                              : ''}{' '}
                            {row?.amount
                              ? formatToMaxFourDigits(row?.amount, true)
                              : 0}
                          </p>
                        </div>
                      </TableCell>

                      <TableCell
                        className={`${
                          !columns?.[1]?.sortable === true &&
                          styles.Table__Cell__Static
                        }`}
                        key={`invoices_periods_table_row_${row.period}_cell_2`}
                        component="th"
                        scope="row"
                      >
                        <div>
                          <p
                            className={`${styles.Table__Cell__InvoicesPeriod__Received}`}
                          >
                            {row?.sites?.[0]?.unit
                              ? ` ${row.sites[0].unit}`
                              : ''}{' '}
                            {row?.received
                              ? formatToMaxFourDigits(row?.received, true)
                              : 0}
                          </p>
                        </div>
                      </TableCell>

                      <TableCell
                        className={`${
                          !columns?.[3]?.sortable === true &&
                          styles.Table__Cell__Static
                        }`}
                        key={`invoices_periods_table_row_${row.period}_cell_4`}
                        component="th"
                        scope="row"
                      >
                        <div>
                          <div>
                            <p
                              className={`${styles.Table__Cell__InvoicesPeriod__Paid}`}
                            >
                              {' '}
                              {row?.paid ? row?.paid : ''}{' '}
                              {t('widgets.tables.utils.paid')}
                            </p>
                            {/* <p className={ `${styles.Table__Cell__Community__Status}`} > {row?.status || false }</p> */}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {spacerHeight > 0 && (
                    <TableRow className={styles.Table__DrawerSpacerRow}>
                      <TableCell colSpan={columns.length}>
                        <div
                          className={styles.Table__DrawerSpacer}
                          style={{ height: spacerHeight }}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>

                <TableFooter
                  className={styles.Table__InvoicesPeriods__Footer}
                  ref={footerRef}
                >
                  <TableRow>
                    <TableCell
                      align="left"
                      className={`${
                        columns[0].sortable &&
                        styles.Table__Footer__Cell__Clickable
                      }`}
                    >
                      <div
                        onClick={() =>
                          columns[0].sortable ? sortRows('period') : null
                        }
                        className={`${
                          columns[0].sortable && rowsOrder[0] === 'period'
                            ? styles.Table__Footer__Cell__Active
                            : styles.Table__Footer__Cell
                        }`}
                      >
                        {storedRows.length}{' '}
                        {storedRows.length === 1
                          ? t('widgets.tables.utils.total_period')
                          : t('widgets.tables.utils.total_periods')}
                        {rowsOrder[0] === 'period' ? (
                          rowsOrder[1] === 'asc' ? (
                            <IconWrapper
                              iconName="chevron-up"
                              className={`${styles.Table__TH__Icon}`}
                            />
                          ) : (
                            <IconWrapper
                              iconName="chevron-down"
                              className={`${styles.Table__TH__Icon}`}
                            />
                          )
                        ) : (
                          <div style={{ height: 32, width: 19 }}></div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell
                      align="left"
                      className={`${
                        columns[1].sortable &&
                        styles.Table__Footer__Cell__Clickable
                      }`}
                    >
                      <div
                        onClick={() =>
                          columns[1].sortable ? sortRows('amount') : null
                        }
                        className={`${
                          columns[1].sortable && rowsOrder[0] === 'amount'
                            ? styles.Table__Footer__Cell__Active
                            : styles.Table__Footer__Cell
                        }`}
                      >
                        {formatToMaxFourDigits(
                          storedRows.reduce(
                            (acc: number, row: any) => acc + row.amount,
                            0
                          ),
                          true
                        )}{' '}
                        {t('widgets.tables.utils.total_due')}
                        {rowsOrder[0] === 'amount' ? (
                          rowsOrder[1] === 'asc' ? (
                            <IconWrapper
                              iconName="chevron-up"
                              className={`${styles.Table__TH__Icon}`}
                            />
                          ) : (
                            <IconWrapper
                              iconName="chevron-down"
                              className={`${styles.Table__TH__Icon}`}
                            />
                          )
                        ) : (
                          <div style={{ height: 32, width: 19 }}></div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell
                      align="left"
                      className={`${
                        columns[2].sortable &&
                        styles.Table__Footer__Cell__Clickable
                      }`}
                    >
                      <div
                        onClick={() =>
                          columns[2].sortable ? sortRows('received') : null
                        }
                        className={`${
                          columns[2].sortable && rowsOrder[0] === 'received'
                            ? styles.Table__Footer__Cell__Active
                            : styles.Table__Footer__Cell
                        }`}
                      >
                        {formatToMaxFourDigits(
                          storedRows.reduce(
                            (acc: number, row: any) => acc + row.received,
                            0
                          ),
                          true
                        )}{' '}
                        {t('widgets.tables.utils.total_received')}
                        {rowsOrder[0] === 'received' ? (
                          rowsOrder[1] === 'asc' ? (
                            <IconWrapper
                              iconName="chevron-up"
                              className={`${styles.Table__TH__Icon}`}
                            />
                          ) : (
                            <IconWrapper
                              iconName="chevron-down"
                              className={`${styles.Table__TH__Icon}`}
                            />
                          )
                        ) : (
                          <div style={{ height: 32, width: 19 }}></div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell align="left"></TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </div>
        </div>
      ) : (
        <div className={`${styles.Table} mb-3`}>
          <div className={`${styles.Table__Title}`}>{title}</div>
          <TableSkeleton rows={rows} context="communities" hasShimmer={false} />
        </div>
      )}
    </div>
  );
}

export default InvoicesPeriodTable;
